import { QubBaseError, requestJson, type Fetch, type Result } from "./http";

export interface User {
  id: string;
  aud: string;
  role: string;
  email: string | null;
  phone: string | null;
  email_confirmed_at: string | null;
  confirmed_at: string | null;
  last_sign_in_at: string | null;
  app_metadata: Record<string, unknown>;
  user_metadata: Record<string, unknown>;
  is_anonymous: boolean;
  banned_until: string | null;
  new_email: string | null;
  factors: {
    id: string;
    friendly_name: string | null;
    factor_type: "totp";
    status: "unverified" | "verified";
    created_at: string;
    updated_at: string;
  }[];
  created_at: string;
  updated_at: string;
}

export interface Session {
  access_token: string;
  token_type: "bearer";
  expires_in: number;
  expires_at: number;
  refresh_token: string;
  user: User;
}

export type AuthChangeEvent =
  | "INITIAL_SESSION"
  | "SIGNED_IN"
  | "SIGNED_OUT"
  | "TOKEN_REFRESHED"
  | "USER_UPDATED"
  | "PASSWORD_RECOVERY"
  | "MFA_CHALLENGE_VERIFIED";

/** Where the session is kept between page loads (localStorage in browsers, memory elsewhere). */
export interface SessionStorage {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

export interface AuthOptions {
  storage?: SessionStorage;
  storageKey?: string;
  persistSession?: boolean;
  autoRefreshToken?: boolean;
  /** Pick up `#access_token=…` after an email link redirect (browsers only). */
  detectSessionInUrl?: boolean;
}

class MemoryStorage implements SessionStorage {
  private readonly map = new Map<string, string>();
  getItem(key: string) {
    return this.map.get(key) ?? null;
  }
  setItem(key: string, value: string) {
    this.map.set(key, value);
  }
  removeItem(key: string) {
    this.map.delete(key);
  }
}

type Listener = (event: AuthChangeEvent, session: Session | null) => void;

function decodeClaims(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1] ?? "";
    const padded = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
}

/** GoTrue-compatible auth client: email/password, magic links & OTP, anonymous users, MFA, admin API. */
export class AuthClient {
  private session: Session | null = null;
  private readonly listeners = new Set<Listener>();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshing: Promise<Result<Session>> | null = null;
  private readonly storage: SessionStorage;
  private readonly storageKey: string;
  private readonly ready: Promise<void>;

  readonly admin: AuthAdminApi;
  readonly mfa: MfaApi;

  constructor(
    private readonly url: string,
    private readonly apiKey: string,
    private readonly fetcher: Fetch,
    private readonly options: AuthOptions & { projectRef: string },
  ) {
    const browser = typeof globalThis.localStorage !== "undefined";
    this.storage =
      options.storage ??
      (browser ? globalThis.localStorage : new MemoryStorage());
    this.storageKey =
      options.storageKey ?? `qb-${options.projectRef}-auth-token`;
    this.admin = new AuthAdminApi(this.url, this.apiKey, this.fetcher);
    this.mfa = new MfaApi(this);
    this.ready = this.initialize();
  }

  /** @internal */
  headers(token?: string): Record<string, string> {
    return {
      apikey: this.apiKey,
      Authorization: `Bearer ${token ?? this.session?.access_token ?? this.apiKey}`,
      "Content-Type": "application/json",
    };
  }

  private async initialize() {
    if (
      this.options.detectSessionInUrl !== false &&
      typeof window !== "undefined" &&
      window.location?.hash.includes("access_token=")
    ) {
      const params = new URLSearchParams(window.location.hash.slice(1));
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");
      if (accessToken && refreshToken) {
        const user = await this.fetchUser(accessToken);
        if (user.data) {
          await this.setSessionInternal(
            {
              access_token: accessToken,
              refresh_token: refreshToken,
              token_type: "bearer",
              expires_in: Number(params.get("expires_in") ?? 3600),
              expires_at: Number(params.get("expires_at") ?? 0),
              user: user.data,
            },
            params.get("type") === "recovery"
              ? "PASSWORD_RECOVERY"
              : "SIGNED_IN",
          );
          window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search,
          );
          return;
        }
      }
    }
    if (this.options.persistSession === false) return;
    const raw = await this.storage.getItem(this.storageKey);
    if (!raw) return;
    try {
      const stored = JSON.parse(raw) as Session;
      this.session = stored;
      if (stored.expires_at * 1000 - Date.now() < 30_000)
        await this.refreshSession();
      else this.scheduleRefresh();
    } catch {
      await this.storage.removeItem(this.storageKey);
    }
  }

  private scheduleRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    if (!this.session || this.options.autoRefreshToken === false) return;
    const ms = Math.max(
      this.session.expires_at * 1000 - Date.now() - 60_000,
      5_000,
    );
    this.refreshTimer = setTimeout(() => void this.refreshSession(), ms);
    (this.refreshTimer as { unref?: () => void }).unref?.();
  }

  private emit(event: AuthChangeEvent) {
    for (const l of this.listeners) l(event, this.session);
  }

  private async setSessionInternal(
    session: Session | null,
    event: AuthChangeEvent,
  ) {
    this.session = session;
    if (this.options.persistSession !== false) {
      if (session)
        await this.storage.setItem(this.storageKey, JSON.stringify(session));
      else await this.storage.removeItem(this.storageKey);
    }
    if (session) this.scheduleRefresh();
    else if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.emit(event);
  }

  onAuthStateChange(callback: Listener) {
    this.listeners.add(callback);
    void this.ready.then(() => callback("INITIAL_SESSION", this.session));
    return {
      data: {
        subscription: { unsubscribe: () => this.listeners.delete(callback) },
      },
    };
  }

  /** The current session (refreshed first if it is about to expire). */
  async getSession(): Promise<{
    data: { session: Session | null };
    error: QubBaseError | null;
  }> {
    await this.ready;
    if (this.session && this.session.expires_at * 1000 - Date.now() < 30_000) {
      const { error } = await this.refreshSession();
      if (error) return { data: { session: null }, error };
    }
    return { data: { session: this.session }, error: null };
  }

  /** Bearer token for other modules (user token when signed in, otherwise the API key). */
  async accessToken(): Promise<string> {
    const { data } = await this.getSession();
    return data.session?.access_token ?? this.apiKey;
  }

  private async fetchUser(token: string): Promise<Result<User>> {
    return requestJson<User>(
      this.fetcher,
      `${this.url}/user`,
      { method: "GET" },
      async () => this.headers(token),
    );
  }

  /** Validates the token with the server and returns the user. */
  async getUser(
    jwt?: string,
  ): Promise<{ data: { user: User | null }; error: QubBaseError | null }> {
    const token = jwt ?? (await this.getSession()).data.session?.access_token;
    if (!token)
      return {
        data: { user: null },
        error: new QubBaseError("Auth session missing", 401, "session_missing"),
      };
    const { data, error } = await this.fetchUser(token);
    return { data: { user: data }, error };
  }

  private async sessionCall(
    path: string,
    body: unknown,
    event: AuthChangeEvent = "SIGNED_IN",
  ) {
    await this.ready;
    const { data, error } = await requestJson<Session | { user: User }>(
      this.fetcher,
      `${this.url}${path}`,
      { method: "POST", body: JSON.stringify(body) },
      async () => this.headers(this.apiKey),
    );
    if (error) return { data: { user: null, session: null }, error };
    if (data && "access_token" in data) {
      await this.setSessionInternal(data, event);
      return { data: { user: data.user, session: data }, error: null };
    }
    return {
      data: {
        user: (data as { user: User } | null)?.user ?? null,
        session: null,
      },
      error: null,
    };
  }

  signUp(credentials: {
    email: string;
    password: string;
    options?: { data?: Record<string, unknown>; emailRedirectTo?: string };
  }) {
    return this.sessionCall("/signup", {
      email: credentials.email,
      password: credentials.password,
      data: credentials.options?.data ?? {},
      redirect_to: credentials.options?.emailRedirectTo,
    });
  }

  signInWithPassword(credentials: { email: string; password: string }) {
    return this.sessionCall("/token?grant_type=password", credentials);
  }

  signInAnonymously(options: { data?: Record<string, unknown> } = {}) {
    return this.sessionCall("/signup", { data: options.data ?? {} });
  }

  /** Emails a magic link and a 6-digit code. */
  async signInWithOtp(credentials: {
    email: string;
    options?: {
      shouldCreateUser?: boolean;
      emailRedirectTo?: string;
      data?: Record<string, unknown>;
    };
  }) {
    const { error } = await requestJson(
      this.fetcher,
      `${this.url}/otp`,
      {
        method: "POST",
        body: JSON.stringify({
          email: credentials.email,
          create_user: credentials.options?.shouldCreateUser ?? true,
          redirect_to: credentials.options?.emailRedirectTo,
          data: credentials.options?.data,
        }),
      },
      async () => this.headers(this.apiKey),
    );
    return { data: { user: null, session: null }, error };
  }

  /** Completes email OTP / magic link / recovery / signup confirmation with a code or token hash. */
  verifyOtp(params: {
    email?: string;
    token?: string;
    token_hash?: string;
    type:
      | "email"
      | "magiclink"
      | "signup"
      | "recovery"
      | "invite"
      | "email_change";
  }) {
    return this.sessionCall(
      "/verify",
      params,
      params.type === "recovery" ? "PASSWORD_RECOVERY" : "SIGNED_IN",
    );
  }

  async resetPasswordForEmail(
    email: string,
    options: { redirectTo?: string } = {},
  ) {
    const { error } = await requestJson(
      this.fetcher,
      `${this.url}/recover`,
      {
        method: "POST",
        body: JSON.stringify({ email, redirect_to: options.redirectTo }),
      },
      async () => this.headers(this.apiKey),
    );
    return { data: {}, error };
  }

  async resend(params: {
    type: "signup" | "email_change";
    email: string;
    options?: { emailRedirectTo?: string };
  }) {
    const { error } = await requestJson(
      this.fetcher,
      `${this.url}/resend`,
      {
        method: "POST",
        body: JSON.stringify({
          type: params.type,
          email: params.email,
          redirect_to: params.options?.emailRedirectTo,
        }),
      },
      async () => this.headers(this.apiKey),
    );
    return { data: {}, error };
  }

  async updateUser(
    attributes: {
      email?: string;
      password?: string;
      data?: Record<string, unknown>;
    },
    options: { emailRedirectTo?: string } = {},
  ) {
    const token = (await this.getSession()).data.session?.access_token;
    if (!token)
      return {
        data: { user: null },
        error: new QubBaseError("Auth session missing", 401, "session_missing"),
      };
    const { data, error } = await requestJson<User>(
      this.fetcher,
      `${this.url}/user`,
      {
        method: "PUT",
        body: JSON.stringify({
          ...attributes,
          redirect_to: options.emailRedirectTo,
        }),
      },
      async () => this.headers(token),
    );
    if (data && this.session)
      await this.setSessionInternal(
        { ...this.session, user: data },
        "USER_UPDATED",
      );
    return { data: { user: data }, error };
  }

  /** Uses a refresh token you already have (e.g. from your server) to establish a session. */
  async setSession(tokens: { access_token: string; refresh_token: string }) {
    const { data: user, error } = await this.fetchUser(tokens.access_token);
    if (error || !user)
      return this.refreshSession({ refresh_token: tokens.refresh_token });
    const claims = decodeClaims(tokens.access_token);
    const exp = Number(claims?.exp ?? Math.floor(Date.now() / 1000) + 3600);
    const session: Session = {
      ...tokens,
      token_type: "bearer",
      expires_at: exp,
      expires_in: exp - Math.floor(Date.now() / 1000),
      user,
    };
    await this.setSessionInternal(session, "SIGNED_IN");
    return { data: { user, session }, error: null };
  }

  async refreshSession(current?: {
    refresh_token: string;
  }): Promise<{
    data: { user: User | null; session: Session | null };
    error: QubBaseError | null;
  }> {
    const refreshToken = current?.refresh_token ?? this.session?.refresh_token;
    if (!refreshToken)
      return {
        data: { user: null, session: null },
        error: new QubBaseError("Auth session missing", 401, "session_missing"),
      };
    this.refreshing ??= requestJson<Session>(
      this.fetcher,
      `${this.url}/token?grant_type=refresh_token`,
      { method: "POST", body: JSON.stringify({ refresh_token: refreshToken }) },
      async () => this.headers(this.apiKey),
    ).finally(() => {
      this.refreshing = null;
    });
    const { data, error } = await this.refreshing;
    if (error || !data) {
      // The refresh token is gone (revoked/reused/expired): the user must sign in again.
      if (error && error.status >= 400 && error.status < 500)
        await this.setSessionInternal(null, "SIGNED_OUT");
      return { data: { user: null, session: null }, error };
    }
    await this.setSessionInternal(data, "TOKEN_REFRESHED");
    return { data: { user: data.user, session: data }, error: null };
  }

  async signOut(options: { scope?: "global" | "local" | "others" } = {}) {
    const token = this.session?.access_token;
    let error: QubBaseError | null = null;
    if (token) {
      ({ error } = await requestJson(
        this.fetcher,
        `${this.url}/logout?scope=${options.scope ?? "local"}`,
        { method: "POST" },
        async () => this.headers(token),
      ));
    }
    if (options.scope !== "others")
      await this.setSessionInternal(null, "SIGNED_OUT");
    return { error: error && error.status !== 401 ? error : null };
  }

  /** @internal used by MfaApi */
  async authedRequest<T>(path: string, init: RequestInit): Promise<Result<T>> {
    const token = (await this.getSession()).data.session?.access_token;
    if (!token)
      return {
        data: null,
        error: new QubBaseError("Auth session missing", 401, "session_missing"),
      };
    return requestJson<T>(this.fetcher, `${this.url}${path}`, init, async () =>
      this.headers(token),
    );
  }

  /** @internal used by MfaApi */
  async applySession(session: Session, event: AuthChangeEvent) {
    await this.setSessionInternal(session, event);
  }

  /** @internal */
  currentSession() {
    return this.session;
  }

  stopAutoRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }
}

/** TOTP multi-factor authentication. */
export class MfaApi {
  constructor(private readonly auth: AuthClient) {}

  enroll(
    params: {
      factorType?: "totp";
      friendlyName?: string;
      issuer?: string;
    } = {},
  ) {
    return this.auth.authedRequest<{
      id: string;
      type: "totp";
      friendly_name: string | null;
      totp: { secret: string; uri: string };
    }>("/factors", {
      method: "POST",
      body: JSON.stringify({
        factor_type: "totp",
        friendly_name: params.friendlyName,
        issuer: params.issuer,
      }),
    });
  }

  challenge(params: { factorId: string }) {
    return this.auth.authedRequest<{
      id: string;
      type: "totp";
      expires_at: number;
    }>(`/factors/${params.factorId}/challenge`, { method: "POST", body: "{}" });
  }

  async verify(params: {
    factorId: string;
    challengeId: string;
    code: string;
  }) {
    const result = await this.auth.authedRequest<Session>(
      `/factors/${params.factorId}/verify`,
      {
        method: "POST",
        body: JSON.stringify({
          challenge_id: params.challengeId,
          code: params.code,
        }),
      },
    );
    if (result.data)
      await this.auth.applySession(result.data, "MFA_CHALLENGE_VERIFIED");
    return result;
  }

  async challengeAndVerify(params: { factorId: string; code: string }) {
    const c = await this.challenge({ factorId: params.factorId });
    if (c.error || !c.data) return { data: null, error: c.error };
    return this.verify({
      factorId: params.factorId,
      challengeId: c.data.id,
      code: params.code,
    });
  }

  unenroll(params: { factorId: string }) {
    return this.auth.authedRequest<{ id: string }>(
      `/factors/${params.factorId}`,
      { method: "DELETE" },
    );
  }

  async listFactors() {
    const { data, error } = await this.auth.getUser();
    const all = data.user?.factors ?? [];
    return {
      data: { all, totp: all.filter((f) => f.status === "verified") },
      error,
    };
  }

  /** Current assurance level and what the user could reach (aal2 when a verified factor exists). */
  getAuthenticatorAssuranceLevel() {
    const session = this.auth.currentSession();
    if (!session)
      return { data: { currentLevel: null, nextLevel: null }, error: null };
    const claims = decodeClaims(session.access_token);
    const currentLevel = (claims?.aal as "aal1" | "aal2" | undefined) ?? "aal1";
    const nextLevel = session.user.factors.some((f) => f.status === "verified")
      ? "aal2"
      : "aal1";
    return { data: { currentLevel, nextLevel }, error: null };
  }
}

/** Requires the service_role key — use only on servers. */
export class AuthAdminApi {
  constructor(
    private readonly url: string,
    private readonly key: string,
    private readonly fetcher: Fetch,
  ) {}

  private call<T>(path: string, init: RequestInit) {
    return requestJson<T>(
      this.fetcher,
      `${this.url}${path}`,
      init,
      async () => ({
        apikey: this.key,
        Authorization: `Bearer ${this.key}`,
        "Content-Type": "application/json",
      }),
    );
  }

  async listUsers(params: { page?: number; perPage?: number } = {}) {
    const { data, error } = await this.call<{ users: User[]; total: number }>(
      `/admin/users?page=${params.page ?? 1}&per_page=${params.perPage ?? 50}`,
      { method: "GET" },
    );
    return {
      data: { users: data?.users ?? [], total: data?.total ?? 0 },
      error,
    };
  }

  async createUser(attributes: {
    email: string;
    password?: string;
    email_confirm?: boolean;
    user_metadata?: Record<string, unknown>;
    app_metadata?: Record<string, unknown>;
    phone?: string;
  }) {
    const { data, error } = await this.call<User>("/admin/users", {
      method: "POST",
      body: JSON.stringify(attributes),
    });
    return { data: { user: data }, error };
  }

  async getUserById(id: string) {
    const { data, error } = await this.call<User>(`/admin/users/${id}`, {
      method: "GET",
    });
    return { data: { user: data }, error };
  }

  async updateUserById(
    id: string,
    attributes: {
      email?: string;
      password?: string;
      email_confirm?: boolean;
      user_metadata?: Record<string, unknown>;
      app_metadata?: Record<string, unknown>;
      ban_duration?: string;
    },
  ) {
    const { data, error } = await this.call<User>(`/admin/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(attributes),
    });
    return { data: { user: data }, error };
  }

  async deleteUser(id: string) {
    const { error } = await this.call(`/admin/users/${id}`, {
      method: "DELETE",
    });
    return { data: {}, error };
  }

  async inviteUserByEmail(
    email: string,
    options: { data?: Record<string, unknown>; redirectTo?: string } = {},
  ) {
    const { data, error } = await this.call<User>("/invite", {
      method: "POST",
      body: JSON.stringify({
        email,
        data: options.data,
        redirect_to: options.redirectTo,
      }),
    });
    return { data: { user: data }, error };
  }
}
